import { useEffect, useState } from 'react';
import type { ReducedMotionSetting } from './game/motion';

/** Mobile control size preference. Multiplies/targets the PREFERRED (not
 * floor) button size useGameLayout.ts aims for -- the 44px SPEC.md §5
 * floor and the "shrink toward the floor only where the viewport truly
 * can't fit the bigger size" fit-math in useGameLayout.ts are completely
 * unaffected by this setting; it only changes the CEILING useGameLayout
 * clamps against (see game/useGameLayout.ts CONTROL_SIZE_PREFERRED_BTN).
 * Scales BOTH the joystick (3x this value = its base diameter) and the
 * D-pad (this value = one button's side length). */
export type ControlSize = 'regular' | 'large' | 'xlarge';

/** Which on-screen touch control drives movement.
 *  - 'joystick' (default): the retro thumb-draggable virtual stick
 *    (game/Joystick.tsx).
 *  - 'dpad': the classic 4-button directional pad (game/DPad.tsx).
 *  - 'swipe': no on-screen control at all -- canvas swipe (GameCanvas
 *    `onSwipe`, always live regardless of this setting) is the only touch
 *    input.
 *
 * Supersedes the old 2-way `controlsMode` field ('dpad-swipe' |
 * 'swipe-only'); `readStored()` below migrates an existing player's saved
 * value instead of silently resetting it: 'dpad-swipe' -> 'joystick' (the
 * new default control takes over the "on-screen control + swipe" slot),
 * 'swipe-only' -> 'swipe'. */
export type ControlScheme = 'joystick' | 'dpad' | 'swipe';

/** PART 4: manual reduced-motion override -- see game/motion.ts's
 * `ReducedMotionSetting`/`resolveEffectiveReducedMotion` for the exact
 * semantics ('auto' follows the OS `prefers-reduced-motion` query, 'on'/
 * 'off' force it either way). Re-exported here (not just imported from
 * motion.ts) so `settings.ts` stays the one place every setting's TYPE is
 * declared, matching ControlSize/ControlScheme above. */
export type { ReducedMotionSetting };

export interface Settings {
  /** Mobile vibration on control press. Feature-detected + no-throw at the
   * call site (game/haptics.ts) regardless of this value. */
  haptics: boolean;
  controlSize: ControlSize;
  controlScheme: ControlScheme;
  /** PART 3: colorblind-safe palette + non-hue P2 marker -- see
   * game/colors.ts's `pacColor`/`COLORBLIND_COLORS`. */
  colorblind: boolean;
  /** PART 4: see the `ReducedMotionSetting` doc comment above. */
  reducedMotion: ReducedMotionSetting;
  /** TURN-INTENT TRACE: alternative opt-in to the `?debug=joystick` query
   * param (see game/trace.ts's module doc comment) -- persists across
   * sessions, takes effect on the next page load/reconnect (the WS URL's
   * `debug=1` flag is only read at connect time, see useRoomSocket.ts).
   * Default false: tracing is off unless explicitly turned on by either
   * mechanism. */
  debugJoystick: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  haptics: true,
  controlSize: 'regular',
  controlScheme: 'joystick',
  colorblind: false,
  reducedMotion: 'auto',
  debugJoystick: false,
};

const STORAGE_KEY = 'pm_settings_v1';

function isControlSize(v: unknown): v is ControlSize {
  return v === 'regular' || v === 'large' || v === 'xlarge';
}
function isControlScheme(v: unknown): v is ControlScheme {
  return v === 'joystick' || v === 'dpad' || v === 'swipe';
}
function isReducedMotionSetting(v: unknown): v is ReducedMotionSetting {
  return v === 'auto' || v === 'on' || v === 'off';
}
/** Maps a value stored under the OLD `controlsMode` key/values to the new
 * `controlScheme` -- see the ControlScheme doc comment for the exact
 * mapping. Returns `null` for anything unrecognised so the caller falls
 * back to the default, same as any other malformed field. */
function migrateControlsMode(v: unknown): ControlScheme | null {
  if (v === 'dpad-swipe') return 'joystick';
  if (v === 'swipe-only') return 'swipe';
  return null;
}

/** Merges whatever validates out of localStorage over the defaults --
 * unknown/malformed keys (older schema, hand-edited storage, a future
 * version) are dropped individually rather than discarding the whole
 * object, so a single bad field never resets every other saved
 * preference. */
function readStored(): Settings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    const controlScheme = isControlScheme(parsed.controlScheme)
      ? parsed.controlScheme
      : (migrateControlsMode(parsed.controlsMode) ?? DEFAULT_SETTINGS.controlScheme);
    return {
      haptics: typeof parsed.haptics === 'boolean' ? parsed.haptics : DEFAULT_SETTINGS.haptics,
      controlSize: isControlSize(parsed.controlSize) ? parsed.controlSize : DEFAULT_SETTINGS.controlSize,
      controlScheme,
      colorblind: typeof parsed.colorblind === 'boolean' ? parsed.colorblind : DEFAULT_SETTINGS.colorblind,
      reducedMotion: isReducedMotionSetting(parsed.reducedMotion) ? parsed.reducedMotion : DEFAULT_SETTINGS.reducedMotion,
      debugJoystick: typeof parsed.debugJoystick === 'boolean' ? parsed.debugJoystick : DEFAULT_SETTINGS.debugJoystick,
    };
  } catch {
    return { ...DEFAULT_SETTINGS }; // storage disabled/unavailable/corrupt -- defaults only
  }
}

function writeStored(s: Settings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // best-effort persistence only; never let this throw into the UI
  }
}

let current: Settings = readStored();
const listeners = new Set<(s: Settings) => void>();

/** Plain (non-React) getter -- for call sites like game/haptics.ts that
 * fire outside a component's render (a pointerdown handler). */
export function getSettings(): Settings {
  return current;
}

/** Single source of truth for every write -- SettingsModal and any future
 * caller all funnel through here, so localStorage and every subscribed
 * component/hook can never drift out of sync with each other. */
export function setSettings(patch: Partial<Settings>): void {
  current = { ...current, ...patch };
  writeStored(current);
  for (const l of listeners) l(current);
}

function subscribe(listener: (s: Settings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React hook: `[settings, setSettings]`, reactive to writes from ANY
 * caller (not just this component's own setter calls) -- so e.g. the
 * SettingsModal and a live joystick/D-pad both re-render off the same
 * change. */
export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [s, setS] = useState<Settings>(current);
  useEffect(() => subscribe(setS), []);
  return [s, setSettings];
}
